import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useOverviewRefresh } from '../../../context/OverviewRefreshContext';
import { fetchDashboardData } from '../api';
import { buildDashboardView, EMPTY_DASHBOARD_DATA } from '../lib';

export function useDashboardData(currency) {
	const intl = useIntl();
	const { setRefreshCallback } = useOverviewRefresh();

	const [data, setData] = useState(EMPTY_DASHBOARD_DATA);
	const [status, setStatus] = useState('loading');
	const [refreshKey, setRefreshKey] = useState(0);

	const refresh = useCallback(() => setRefreshKey(key => key + 1), []);

	useEffect(() => {
		setRefreshCallback(refresh);
		return () => setRefreshCallback(null);
	}, [setRefreshCallback, refresh]);

	useEffect(() => {
		// Drops the result of a request that finished after a newer one started.
		let ignore = false;
		setStatus('loading');

		fetchDashboardData()
			.then(result => {
				if (ignore) return;
				setData(result);
				setStatus('success');
			})
			.catch(error => {
				if (ignore) return;
				console.error('Failed to load dashboard data:', error);
				setData(EMPTY_DASHBOARD_DATA);
				setStatus('error');
			});

		return () => {
			ignore = true;
		};
	}, [refreshKey]);

	const view = useMemo(
		() => buildDashboardView(data, currency, intl),
		[data, currency, intl],
	);

	return {
		...view,
		isLoading: status === 'loading',
		isError: status === 'error',
		refresh,
	};
}
